/**
 * Tarama ekraninin alt istatistik seridi + "Bugunun One Cikanlari".
 *
 * Her deger GERCEKTIR:
 *   - Tarama Kapsami: backend'in taradigi / evrendeki sembol sayisi.
 *   - Calisma Suresi: /api/health `started_at`'tan (sunucu sureci).
 *   - Veri Kaynagi: taramanin kaynagi + WebSocket durumu.
 *   - Guncelleme: arayuzun gercek yoklama araligi (App.tsx POLL_MS).
 *   - One cikanlar: taranan evrende 24s en cok yukselen likit pariteler.
 */

import { useEffect, useState } from "react";
import type { MarketOverview, SignalScan } from "../api/types";
import { pct } from "../lib/format";
import { IconBolt, IconClock, IconDatabase, IconFlame, IconTarget } from "./Ui";

function uptime(startedAt: number, now: number): string {
  const minutes = Math.max(0, Math.floor((now - startedAt) / 60_000));
  if (minutes < 60) return `${minutes} dk`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} sa ${minutes % 60} dk`;
  return `${Math.floor(hours / 24)} gün ${hours % 24} sa`;
}

export function ScanFooter({
  scan,
  overview,
  startedAt,
  pollMs,
  onSelect,
}: {
  scan: SignalScan | null;
  overview: MarketOverview | null;
  startedAt: number | null;
  pollMs: number;
  onSelect: (symbol: string) => void;
}) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 60_000);
    return () => window.clearInterval(id);
  }, []);

  if (!scan) return null;

  const live = scan.websocket_status === "CONNECTED";

  return (
    <div className="scan-footer">
      <div className="scan-stats">
        <div className="scan-stat" data-tone="info">
          <span className="scan-stat-icon">
            <IconTarget />
          </span>
          <span className="scan-stat-body">
            <span className="scan-stat-label">Tarama Kapsamı</span>
            <strong>
              {scan.scanned} / {scan.universe_size}
            </strong>
            <span className="scan-stat-sub">Sembol aktif</span>
          </span>
        </div>
        <div className="scan-stat" data-tone="info">
          <span className="scan-stat-icon">
            <IconClock />
          </span>
          <span className="scan-stat-body">
            <span className="scan-stat-label">Çalışma Süresi</span>
            <strong>{startedAt ? uptime(startedAt, now) : "—"}</strong>
            <span className="scan-stat-sub">Otomatik tarama</span>
          </span>
        </div>
        <div className="scan-stat" data-tone="info">
          <span className="scan-stat-icon">
            <IconDatabase />
          </span>
          <span className="scan-stat-body">
            <span className="scan-stat-label">Veri Kaynağı</span>
            <strong>{scan.source === "MOCK" ? "MOCK" : "Binance"}</strong>
            <span className="scan-stat-sub">{live ? "Gerçek zamanlı" : "REST yedek"}</span>
          </span>
        </div>
        <div className="scan-stat" data-tone="info">
          <span className="scan-stat-icon">
            <IconBolt />
          </span>
          <span className="scan-stat-body">
            <span className="scan-stat-label">Güncelleme</span>
            <strong>{Math.round(pollMs / 1000)} saniye</strong>
            <span className="scan-stat-sub">Canlı veri akışı</span>
          </span>
        </div>
      </div>

      {overview && overview.top_gainers.length > 0 ? (
        <div className="scan-highlights">
          <span className="scan-highlights-title">
            <IconFlame />
            Bugünün Öne Çıkanları
          </span>
          <div className="scan-highlights-list">
            {overview.top_gainers.map((tick, index) => (
              <button
                key={tick.symbol}
                type="button"
                className="scan-highlight"
                onClick={() => onSelect(tick.symbol)}
                title={`${tick.symbol} — 24s ${pct(tick.change_24h_pct)}`}
              >
                <span className="scan-highlight-rank">{index + 1}</span>
                <strong>{tick.base_asset}</strong>
                <span className={tick.change_24h_pct >= 0 ? "up" : "down"}>
                  {pct(tick.change_24h_pct, 1)}
                </span>
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
